import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

interface PreviewState {
  enabled: boolean
  draft: Record<string, unknown> | null
}

const PreviewContext = createContext<PreviewState>({ enabled: false, draft: null })

/** Wraps the app. Inert on the published site; inside the CMS preview iframe it
 *  subscribes to live draft content and re-hydrates the page as the editor types.
 *  `enabled` starts false so the first client render matches the pre-rendered
 *  (published) HTML and hydration does not mismatch. */
export function PreviewProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false)
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null)

  useEffect(() => {
    const inPreview =
      new URLSearchParams(window.location.search).has('cms-preview') ||
      window.self !== window.top
    if (!inPreview) return
    setEnabled(true)

    function onMessage(event: MessageEvent) {
      const data = event.data as { type?: string; content?: Record<string, unknown> }
      if (data?.type === 'cms:draft' && data.content) setDraft(data.content)
    }
    window.addEventListener('message', onMessage)
    window.parent?.postMessage({ type: 'cms:ready' }, '*')
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <PreviewContext.Provider value={{ enabled, draft }}>
      {children}
    </PreviewContext.Provider>
  )
}

/** Returns published content normally; the live draft when previewing the same
 *  page (matched by `page_id`). Templates call this once per page. */
export function useEditableContent<T extends Record<string, unknown>>(published: T): T {
  const { enabled, draft } = useContext(PreviewContext)
  if (enabled && draft && draft.page_id === published.page_id) {
    return { ...published, ...draft } as T
  }
  return published
}
